/**
 * マスタ管理 — ユーザータブ。
 *
 * 2026-09-02 改修:
 * - 編集・追加はポップアップ(モーダル)で行う
 * - 「ロール」「マスタ管理」の設定は廃止。権限は役割(flow_role)から決まり、
 *   **承認者はすべての権限を持つ**(マスタ管理を含む)
 * - 承認者・経理を選ぶと、所属事業部は自動的に全事業部になる(全社を見るため)
 * - アカウントを削除できる(退職者は「無効」を推奨する注意つき)
 *
 * 2026-09-03 改修(発注者指示):
 * - 並び順を 承認者 → 経理 → 部門長 → 一般 にする
 * - ログインIDは**社内ID**を入力する(内部で `<社内ID>@ドメイン` に変換)。
 *   本人の**メールアドレス**は連絡先として別の欄にする
 * - 所属事業部の欄が2つあって紛らわしいので1つにまとめる(主所属＝所属)
 * - 「責任者を務める事業部」は廃止し、部門長は所属した事業部の申請を確認する
 * - 「LINEで送る」でLINEを開いて案内を送る(コピーも可。メール送信は廃止)
 * - パスワードは個人ごと。承認者は控えを一覧で確認できる
 * - スマホは表ではなくカードで並べる(横スクロールしない。メールは出さない)
 */
import { useEffect, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import Modal from '@core/components/Modal';
import { useMasters } from './MastersProvider';
import { deleteUserProfile, fetchUsers, saveUserProfile, type UserProfileInput } from './masterRepo';
import { createAuthUser } from './createAuthUser';
import AccountNoticeModal, { buildAccountNotice } from './AccountNoticeModal';
import PasswordResetModal from './PasswordResetModal';
import { deleteAuthUser } from './resetUserPassword';
import {
  deleteResetRequest,
  deleteUserSecret,
  fetchDefaultPassword,
  fetchResetRequests,
  fetchUserSecrets,
  recordPassword,
  type ResetRequest,
  type UserSecret,
} from './secretsRepo';
import { useToast } from '@core/components/Toast';
import { useIsMobile } from '@core/lib/useIsMobile';
import { useAuth } from '@core/auth/AuthProvider';
import { emailToLoginId, isValidLoginId, loginIdToEmail } from '@core/lib/loginId';
import { normalizePassword } from '@core/lib/password';
import {
  FLOW_ROLES,
  FLOW_ROLE_BADGE_CLASS,
  FLOW_ROLE_LABELS,
  flowRoleFromLegacy,
  permissionsForUser,
  type DepartmentId,
  type FlowRole,
  type User,
} from '@core/lib/types';

type FormState = UserProfileInput & { uid: string | null; login_id: string; password: string };

/** 全社を見る役割(承認者・経理)は、所属事業部を全事業部にそろえる */
const ALL_DEPT_ROLES: FlowRole[] = ['approver', 'accounting'];

/** 一覧の並び順(2026-09-03 指示 / 2026-09-07 追加): 承認者 → 経理 → 部門長 → 一般 → 未所属 */
const ROLE_ORDER: Record<FlowRole, number> = { approver: 0, accounting: 1, bucho: 2, member: 3 };

const roleOf = (u: User): FlowRole => u.flow_role ?? flowRoleFromLegacy(u.role, u.is_accounting);
/** 未所属＝所属事業部が1つも無い(名簿から登録した直後など)。一覧では一番下にまとめる */
const isUnassigned = (u: Pick<User, 'department_ids'>): boolean => u.department_ids.length === 0;
const orderOf = (u: User): number => (isUnassigned(u) ? 4 : ROLE_ORDER[roleOf(u)]);

function emptyForm(defaultDept: DepartmentId): FormState {
  return {
    uid: null,
    login_id: '',
    password: '',
    email: '',
    contact_email: '',
    display_name: '',
    role: 'member',
    flow_role: 'member' as FlowRole,
    is_accounting: false,
    primary_department_id: defaultDept,
    department_ids: [defaultDept],
    manager_department_ids: [],
    active: true,
  };
}

export default function UsersTab() {
  const { departments, departmentName } = useMasters();
  const { user: me } = useAuth();
  const isMobile = useIsMobile();
  const toast = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [secrets, setSecrets] = useState<Record<string, UserSecret>>({});
  const [requests, setRequests] = useState<ResetRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  /** パスワード再設定の対象 */
  const [pwReset, setPwReset] = useState<User | null>(null);
  /** LINEで送る文章(2026-09-03 指示) */
  const [notice, setNotice] = useState<string | null>(null);
  /** 初期パスワードの既定値(全社統一。2026-09-03 指示) */
  const [defaultPw, setDefaultPw] = useState('');

  const firstDept = (departments[0]?.id ?? 'honbu') as DepartmentId;
  const allDeptIds = departments.map((d) => d.id);

  async function load() {
    setLoading(true);
    try {
      const [list, sec, reqs, pw] = await Promise.all([
        fetchUsers(),
        fetchUserSecrets().catch(() => ({})),
        fetchResetRequests().catch(() => []),
        fetchDefaultPassword().catch(() => ''),
      ]);
      setDefaultPw(pw);
      setUsers([...list].sort((a, b) => orderOf(a) - orderOf(b) || a.display_name.localeCompare(b.display_name, 'ja')));
      setSecrets(sec);
      setRequests(reqs);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'ユーザーの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleDept(list: DepartmentId[], id: DepartmentId): DepartmentId[] {
    return list.includes(id) ? list.filter((d) => d !== id) : [...list, id];
  }

  /** 役割を変えたときに、所属事業部と経理フラグを自動で合わせる */
  function changeFlowRole(f: FormState, next: FlowRole): FormState {
    const toAll = ALL_DEPT_ROLES.includes(next);
    return {
      ...f,
      flow_role: next,
      is_accounting: next === 'accounting',
      department_ids: toAll ? [...allDeptIds] : f.department_ids,
      primary_department_id:
        toAll && (f.primary_department_id === null || !allDeptIds.includes(f.primary_department_id))
          ? firstDept
          : f.primary_department_id,
      // 部門長は「所属事業部＝担当する事業部」。承認者は任意で担当事業部を持てる(2026-09-08)。それ以外は持たない
      manager_department_ids: next === 'bucho' ? [...f.department_ids] : next === 'approver' ? f.manager_department_ids : [],
    };
  }

  /** 所属事業部を1つ選ぶ(主所属＝所属。2026-09-03 指示で欄を1つにした)。空なら未所属(2026-09-07) */
  function changeDepartment(f: FormState, id: DepartmentId | ''): FormState {
    if (id === '') return { ...f, primary_department_id: null, department_ids: [], manager_department_ids: [] };
    return {
      ...f,
      primary_department_id: id,
      department_ids: [id],
      // 部門長は所属した事業部の申請を確認する(2026-09-03 指示で欄を一本化)
      manager_department_ids: f.flow_role === 'bucho' ? [id] : [],
    };
  }

  /** 承認者が部門長として担当する事業部(2026-09-08 指示)。所属は全事業部のまま、担当だけを選ぶ */
  function toggleApproverDept(f: FormState, id: DepartmentId): FormState {
    return { ...f, manager_department_ids: toggleDept(f.manager_department_ids, id) };
  }

  /** 部門長は複数の事業部を見ることがあるので、チェックで選べるようにする */
  function toggleBuchoDept(f: FormState, id: DepartmentId): FormState {
    const next = toggleDept(f.department_ids, id);
    const list = next.length > 0 ? next : [id];
    return {
      ...f,
      department_ids: list,
      manager_department_ids: [...list],
      primary_department_id:
        f.primary_department_id !== null && list.includes(f.primary_department_id) ? f.primary_department_id : (list[0] as DepartmentId),
    };
  }

  async function save() {
    if (!form) return;
    const loginId = form.login_id.trim().toLowerCase();
    if (!form.uid && !isValidLoginId(loginId)) {
      toast('社内IDは英小文字・数字で入力してください(例: suzuki)');
      return;
    }
    if (!form.display_name.trim()) {
      toast('表示名は必須です');
      return;
    }
    if (form.contact_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email.trim())) {
      toast('メールアドレスの形式が正しくありません');
      return;
    }
    // 一般は未所属(あとで決める)でも保存できる。部門長は担当する事業部が要る(2026-09-07)
    if (form.flow_role === 'bucho' && form.department_ids.length === 0) {
      toast('部門長は所属事業部を選んでください');
      return;
    }
    if (!form.uid && form.password.length < 8) {
      toast('初期パスワードは8文字以上にしてください');
      return;
    }

    setBusy(true);
    try {
      let uid = form.uid;
      const email = form.uid ? form.email : loginIdToEmail(loginId);
      if (!uid) {
        uid = await createAuthUser(email, form.password);
      }
      const { uid: _uid, login_id: _loginId, password: _password, ...profile } = form;
      // 権限は役割から決まる(承認者はすべての権限)
      // 役割を変えたら権限は作り直す(2026-09-10: 経理にしたのに経理の権限が付かず、支払い一覧が空になっていた)
      const before = users.find((u) => u.id === uid);
      const roleChanged = !before || before.flow_role !== form.flow_role;
      const permissions = permissionsForUser(
        form.flow_role ?? 'member',
        roleChanged ? undefined : before?.permissions,
        form.role,
        form.flow_role === 'accounting',
      );
      await saveUserProfile(uid, {
        ...profile,
        email,
        contact_email: form.contact_email?.trim() || null,
        permissions,
      });
      // パスワードの控え(承認者だけが見られる。2026-09-03 指示)
      if (!form.uid) await recordPassword(uid, form.password, 'admin', me?.display_name);
      // 初期パスワードのままなので、初回ログイン時に本人に決めてもらう(2026-09-08 指示)
      if (!form.uid) await updateDoc(doc(getDb(), 'users', uid), { password_set_at: null }).catch(() => undefined);
      await load();
      setForm(null);
      toast(form.uid ? 'ユーザーを更新しました' : 'ユーザーを作成しました');
    } catch (e) {
      const message = e instanceof Error ? e.message : '保存に失敗しました';
      toast(message.includes('email-already-in-use') ? 'この社内IDは既に使われています' : message);
    } finally {
      setBusy(false);
    }
  }

  /** アカウントの削除。過去の記録を残すなら「無効」を使う */
  async function remove(u: User) {
    const ok = window.confirm(
      `${u.display_name} さんのアカウントを削除します。\n\n` +
        '・ログインできなくなります\n' +
        '・過去の申請に残る氏名が表示されなくなります\n\n' +
        '記録を残したい場合は、削除せず「無効」にしてください。削除してよろしいですか?',
    );
    if (!ok) return;
    setBusy(true);
    try {
      // 先にログイン用アカウントを消す(控えのパスワードで本人として入り直す)。
      // 消し忘れると、同じ社内IDで作り直せなくなる(「既に使われています」)
      let authLeft = false;
      const pw = secrets[u.id]?.password;
      if (pw) {
        try {
          await deleteAuthUser(u.email, pw);
        } catch {
          authLeft = true;
        }
      } else {
        authLeft = true;
      }
      await deleteUserProfile(u.id);
      await deleteUserSecret(u.id).catch(() => undefined);
      await load();
      setForm(null);
      toast(
        authLeft
          ? `${u.display_name} さんを削除しました(ログイン用アカウントが残ったため、社内ID「${emailToLoginId(u.email)}」は当面使えません)`
          : `${u.display_name} さんを削除しました`,
      );
    } catch (e) {
      toast(e instanceof Error ? e.message : '削除に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  /** LINEなどに貼り付ける案内の文章を出す(2026-09-03 指示。メール送信は廃止) */
  function showNotice(f: FormState) {
    setNotice(
      buildAccountNotice({
        displayName: f.display_name || '(表示名未入力)',
        loginId: f.uid ? emailToLoginId(f.email) : f.login_id.trim().toLowerCase(),
        password: f.password || secrets[f.uid ?? '']?.password || '',
        url: window.location.origin,
      }),
    );
  }

  function openEdit(u: User) {
    setForm({
      uid: u.id,
      login_id: emailToLoginId(u.email),
      password: '',
      email: u.email,
      contact_email: u.contact_email ?? '',
      display_name: u.display_name,
      role: u.role,
      flow_role: roleOf(u),
      is_accounting: u.is_accounting,
      primary_department_id: u.primary_department_id,
      department_ids: [...u.department_ids],
      manager_department_ids: [...u.manager_department_ids],
      active: u.active,
    });
  }

  const editingUser = form?.uid ? users.find((u) => u.id === form.uid) : undefined;
  const isAllDeptRole = form ? ALL_DEPT_ROLES.includes(form.flow_role ?? 'member') : false;
  const isBucho = form?.flow_role === 'bucho';

  return (
    <>
      {requests.length > 0 && (
        <div className="card">
          <h3>パスワード再設定の依頼</h3>
          <p className="hint" style={{ marginBottom: 10 }}>
            ログイン画面から「パスワードを忘れた」と申し出があった分です。新しいパスワードを発行して本人に伝えたら、「対応済み」を押してください。
          </p>
          <table>
            <tbody>
              {requests.map((r) => {
                const target = users.find((u) => emailToLoginId(u.email) === r.login_id);
                return (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <b>{target?.display_name ?? '(見つかりません)'}</b>
                      <div className="hint">社内ID: {r.login_id}</div>
                    </td>
                    <td className="hint">{r.note}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {target && (
                        <button className="txn-link" onClick={() => setPwReset(target)}>
                          パスワードを再設定
                        </button>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        className="txn-link"
                        onClick={() =>
                          void deleteResetRequest(r.id)
                            .then(load)
                            .catch(() => toast('削除できませんでした'))
                        }
                      >
                        対応済み
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          ユーザー
          <button className="btn-line" style={{ marginLeft: 'auto' }} onClick={() => setShowPw((v) => !v)}>
            {showPw ? 'パスワードを隠す' : 'パスワードを表示'}
          </button>
          <button className="btn-line" onClick={() => setForm({ ...emptyForm(firstDept), password: defaultPw })}>
            ユーザーを追加
          </button>
        </h3>
        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : isMobile ? (
          /* スマホ(2026-09-03 指示): 表だと横にはみ出すのでカードにする。
             メールアドレスは表示しない(編集画面で確認できる)。タップで編集 */
          <div className="user-cards">
            {users.map((u) => {
              const sec = secrets[u.id];
              return (
                <button key={u.id} className="user-card" onClick={() => openEdit(u)}>
                  <div className="user-card-top">
                    <b>{u.display_name}</b>
                    <span className={`badge ${FLOW_ROLE_BADGE_CLASS[roleOf(u)]}`}>{FLOW_ROLE_LABELS[roleOf(u)]}</span>
                    <span className={`badge ${u.active ? 'b-ok' : 'b-ng'}`}>{u.active ? '有効' : '無効'}</span>
                  </div>
                  <div className="user-card-row">
                    <span className="k">社内ID</span>
                    <span>{emailToLoginId(u.email)}</span>
                  </div>
                  <div className="user-card-row">
                    <span className="k">所属</span>
                    <span>
                      {u.department_ids.length === departments.length ? (
                        <>
                          全事業部
                          {roleOf(u) === 'approver' && u.manager_department_ids.length > 0 && (
                            <span className="hint" style={{ display: 'block', fontSize: 10 }}>
                              部門長: {u.manager_department_ids.map(departmentName).join('・')}
                            </span>
                          )}
                        </>
                      ) : isUnassigned(u) ? (
                        <span className="badge b-pend">未所属</span>
                      ) : (
                        u.department_ids.map(departmentName).join('、')
                      )}
                    </span>
                  </div>
                  <div className="user-card-row">
                    <span className="k">パスワード</span>
                    <span>
                      {sec ? (
                        <>
                          <span style={{ fontFamily: 'monospace' }}>{showPw ? sec.password : '••••••••'}</span>
                          <span className="hint"> / {sec.set_by === 'self' ? '本人が変更' : '承認者が発行'}</span>
                        </>
                      ) : (
                        <span className="hint">控えなし</span>
                      )}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>表示名</th>
                <th>社内ID</th>
                <th>メールアドレス</th>
                <th>役割</th>
                <th>所属事業部</th>
                <th>パスワード</th>
                <th>状態</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const sec = secrets[u.id];
                return (
                  <tr key={u.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{u.display_name}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{emailToLoginId(u.email)}</td>
                    <td className="hint">{u.contact_email || '—'}</td>
                    <td>
                      <span className={`badge ${FLOW_ROLE_BADGE_CLASS[roleOf(u)]}`}>{FLOW_ROLE_LABELS[roleOf(u)]}</span>
                    </td>
                    <td className="hint">
                      {u.department_ids.length === departments.length ? (
                        <>
                          全事業部
                          {/* 承認者が部門長として担当する事業部(2026-09-09 指示: 小さく下に) */}
                          {roleOf(u) === 'approver' && u.manager_department_ids.length > 0 && (
                            <span style={{ display: 'block', fontSize: 10, whiteSpace: 'nowrap' }}>
                              部門長: {u.manager_department_ids.map(departmentName).join('・')}
                            </span>
                          )}
                        </>
                      ) : isUnassigned(u) ? (
                        <span className="badge b-pend">未所属</span>
                      ) : (
                        u.department_ids.map(departmentName).join('、')
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {sec ? (
                        <>
                          <span style={{ fontFamily: 'monospace' }}>{showPw ? sec.password : '••••••••'}</span>
                          <span className="hint" style={{ marginLeft: 6, whiteSpace: 'nowrap' }}>{sec.set_by === 'self' ? '本人が変更' : '承認者が発行'}</span>
                        </>
                      ) : (
                        <span className="hint">控えなし</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${u.active ? 'b-ok' : 'b-ng'}`}>{u.active ? '有効' : '無効'}</span>
                    </td>
                    <td>
                      <button className="txn-link" onClick={() => openEdit(u)}>
                        編集
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="hint" style={{ marginTop: 10 }}>
          役割は承認フローでの担当です(一般=申請のみ / 部門長=自分の事業部を確認 / 承認者=承認と完了確認 /
          経理=支払いと確定)。承認者はマスタ管理を含むすべての権限を持ちます。承認者・経理は全事業部が対象です。部門長は、所属した事業部の申請が確認として回ります。
          <br />
          ログインは<b>社内ID</b>で行います(メールアドレスではログインできません)。メールアドレスは案内の送信先です。
          パスワードは個人ごとに設定し、この画面で確認できます(承認者のみ)。
          <br />
          退職者はアカウントを削除せず「無効」にすると、過去の申請・承認の記録が残ります。
        </p>
      </div>

      {/* 追加・編集はポップアップ */}
      {form && (
        <Modal onClose={() => (busy ? undefined : setForm(null))} width="min(720px, 94vw)" showClose>
          <h3 style={{ marginBottom: 12 }}>
            {form.uid ? `ユーザーを編集 — ${form.display_name}` : 'ユーザーを追加'}
          </h3>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <div>
              <label>社内ID(ログインに使います)</label>
              <input
                value={form.login_id}
                disabled={!!form.uid}
                placeholder="例: suzuki"
                onChange={(e) => setForm({ ...form, login_id: e.target.value })}
              />
              {!form.uid && <p className="hint">英小文字・数字で入力します。あとから変更できません。</p>}
            </div>
            <div>
              <label>表示名</label>
              <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
            </div>
            <div>
              <label>メールアドレス(案内の送信先)</label>
              <input
                type="email"
                value={form.contact_email ?? ''}
                placeholder="例: suzuki@smartcontrol.co.jp"
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              />
            </div>
            {!form.uid && (
              <div>
                <label>初期パスワード(全社共通。本人に伝える)</label>
                <input
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: normalizePassword(e.target.value) })}
                />
              </div>
            )}
            <div>
              <label>役割(承認フローでの担当)</label>
              <select
                value={form.flow_role ?? 'member'}
                onChange={(e) => setForm(changeFlowRole(form, e.target.value as FlowRole))}
              >
                {FLOW_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {FLOW_ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>状態</label>
              <select value={form.active ? '1' : '0'} onChange={(e) => setForm({ ...form, active: e.target.value === '1' })}>
                <option value="1">有効</option>
                <option value="0">無効(ログインできません)</option>
              </select>
            </div>
            {isBucho ? (
              <div className="fw">
                <label>所属事業部(この事業部の申請が、確認としてこの人に回ります)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {departments.map((d) => (
                    <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--ink)' }}>
                      <input
                        type="checkbox"
                        style={{ width: 'auto' }}
                        checked={form.department_ids.includes(d.id)}
                        onChange={() => setForm(toggleBuchoDept(form, d.id))}
                      />
                      {d.name}
                    </label>
                  ))}
                </div>
                <p className="hint">複数の事業部を見る部門長は、いくつでも選べます。</p>
              </div>
            ) : form.flow_role === 'approver' ? (
              <div className="fw">
                <label>部門長として担当する事業部(任意。この事業部の申請の部門長確認がこの人に回ります)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {departments.map((d) => (
                    <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--ink)' }}>
                      <input
                        type="checkbox"
                        style={{ width: 'auto' }}
                        checked={form.manager_department_ids.includes(d.id)}
                        onChange={() => setForm(toggleApproverDept(form, d.id))}
                      />
                      {d.name}
                    </label>
                  ))}
                </div>
                <p className="hint">所属・閲覧は全事業部のまま。承認(②)は全事業部の申請に対して行えます。担当事業部の申請は部門長確認(①)→承認(②)の順に、同じ人が続けて行えます。</p>
              </div>
            ) : (
              <div>
                <label>所属事業部(申請・閲覧できる範囲)</label>
                {isAllDeptRole ? (
                  <input value="全事業部(承認者・経理のため自動)" disabled />
                ) : (
                  <select
                    value={form.primary_department_id ?? ''}
                    onChange={(e) => setForm(changeDepartment(form, e.target.value as DepartmentId | ''))}
                  >
                    <option value="">未所属(あとで決める)</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button className="btn-green" onClick={() => void save()} disabled={busy}>
              {busy ? '保存中…' : '保存'}
            </button>
            <button className="btn-line" onClick={() => setForm(null)} disabled={busy}>
              キャンセル
            </button>
            <button className="btn-line-send" onClick={() => showNotice(form)} disabled={busy}>
              LINEで送る
            </button>
            {editingUser && (
              <button className="btn-line" disabled={busy} onClick={() => setPwReset(editingUser)}>
                パスワードを再設定
              </button>
            )}
            {editingUser && (
              <button className="btn-line danger" disabled={busy} onClick={() => void remove(editingUser)}>
                このアカウントを削除
              </button>
            )}
          </div>
          <p className="hint" style={{ marginTop: 10 }}>
            「LINEで送る」を押すとLINEが開き、送る相手を選ぶだけで案内(URL・社内ID・パスワード)を送れます。コピーして他の方法で渡すこともできます。
          </p>
        </Modal>
      )}

      {notice && <AccountNoticeModal text={notice} onClose={() => setNotice(null)} />}

      {pwReset && (
        <PasswordResetModal
          target={pwReset}
          onDone={() => void load()}
          onClose={() => setPwReset(null)}
        />
      )}
    </>
  );
}
